import React, { useEffect, useState } from "react";
import { getTransaction } from "../services/moka";
import Swal from "sweetalert2";
import DatePicker from "./form/date-picker";

interface TestModalProps {
  outlet: any;
  isOpen: boolean;
  onClose: () => void;
}

export const TestModal: React.FC<TestModalProps> = ({ outlet, isOpen, onClose }) => {
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  // Animation state
  const [visible, setVisible] = useState<boolean>(false);
  const [animating, setAnimating] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      setAnimating(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setAnimating(false), 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!startDate || !endDate) return;
    setLoading(true);

    const untilDate = new Date(endDate);
    untilDate.setDate(untilDate.getDate() + 1);
    const untilStr = untilDate.toISOString().split("T")[0];

    // console.log("Tenant id", outlet.TenantId)
    // console.log("End date (adjusted)", untilStr)
    try {
      const response = await getTransaction({
        TenantId: outlet.TenantId,
        Since: startDate,
        Until: untilStr,
      });

      // console.log("response", response?.data.payments.length);

      if (response) {
        await Swal.fire({
          title: "Sukses",
          html: `<div class="text-left"><p class="font-semibold">Transaction : ${response.data.payments?.length ?? 0}</p></div>`,
          icon: "success",
          confirmButtonText: "OK",
          confirmButtonColor: "#ea580c",
        });
        onClose();
      }
    } catch (err: any) {
      // console.error("Test failed", err);
      const message = err.response?.data?.Message || err.response?.data?.message || "Failed to retrieve transaction data.";
      Swal.fire({
        title: "Test Failed",
        text: message,
        icon: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!animating && !isOpen) return null;

  return (
    <>
      <style>{`
        @keyframes tmSlideDown {
          from { opacity: 0; transform: translateY(-40px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)     scale(1);    }
        }
        @keyframes tmSlideUp {
          from { opacity: 1; transform: translateY(0)     scale(1);    }
          to   { opacity: 0; transform: translateY(-40px) scale(0.97); }
        }
        .tm-box-enter  { animation: tmSlideDown 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .tm-box-exit   { animation: tmSlideUp   0.3s  ease-in forwards; }
      `}</style>

      <div
        className={`fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] overflow-y-auto transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      >
        <div className="flex justify-center items-start min-h-screen py-10 px-4">
          <div
            className={visible ? "tm-box-enter" : "tm-box-exit"}
            style={{ width: "100%", maxWidth: "24rem" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/50">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Test Outlet</h2>
                  <p className="text-xs text-gray-500 font-medium">{outlet?.OutletName}</p>
                </div>
              </div>

              <div className="space-y-4 text-left">
                <DatePicker
                  id="start-date"
                  label="Start Date"
                  placeholder="Select start date"
                  staticOption={false}
                  onChange={(_dates, dateStr) => setStartDate(dateStr)}
                />

                <DatePicker
                  id="end-date"
                  label="End Date"
                  placeholder="Select end date"
                  staticOption={false}
                  onChange={(_dates, dateStr) => setEndDate(dateStr)}
                />

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-bold rounded-xl transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!startDate || !endDate || loading}
                    className="flex-1 px-4 py-2.5 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white text-sm font-bold rounded-xl shadow-lg shadow-orange-500/30 transition-all flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : null}
                    {loading ? "Testing..." : "Submit Test"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
